import { ExpiryStatus } from '@rest-hooks/endpoint';
import { denormalize, isEntity, WeakListMap } from '@rest-hooks/normalizr';
import { inferResults } from '@rest-hooks/normalizr';
import { unsetDispatch } from '@rest-hooks/use-enhanced-reducer';
import { createUnsubscription, createSubscription } from './createSubscription.js';
import createReceive from './createReceive.js';
import createReset from './createReset.js';
import createFetch from './createFetch.js';
import createInvalidate from './createInvalidate.js';
import selectMeta from '../state/selectors/selectMeta.js';
/**
 * Imperative control of Rest Hooks store
 * @see https://resthooks.io/docs/api/Controller
 */

export default class Controller {
  constructor() {
    var _this = this;

    let {
      dispatch = unsetDispatch,
      globalCache = {
        entities: {},
        results: {}
      }
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    this.fetch = function (endpoint) {
      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      const action = createFetch(endpoint, {
        args
      });

      _this.dispatch(action);

      return action.meta.promise;
    };

    this.invalidate = function (endpoint) {
      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }

      return args[0] !== null ? _this.dispatch(createInvalidate(endpoint, {
        args: args
      })) : Promise.resolve();
    };

    this.resetEntireStore = () => this.dispatch(createReset());

    this.receive = function (endpoint) {
      for (var _len3 = arguments.length, rest = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
        rest[_key3 - 1] = arguments[_key3];
      }

      const response = rest[rest.length - 1];
      const action = createReceive(endpoint, {
        args: rest.slice(0, rest.length - 1),
        response
      });
      return _this.dispatch(action);
    };

    this.receiveError = function (endpoint) {
      for (var _len4 = arguments.length, rest = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
        rest[_key4 - 1] = arguments[_key4];
      }

      const response = rest[rest.length - 1];
      const action = createReceive(endpoint, {
        args: rest.slice(0, rest.length - 1),
        response,
        error: true
      });
      return _this.dispatch(action);
    };

    this.resolve = (endpoint, meta) => {
      return this.dispatch(createReceive(endpoint, meta));
    };

    this.subscribe = function (endpoint) {
      for (var _len5 = arguments.length, args = new Array(_len5 > 1 ? _len5 - 1 : 0), _key5 = 1; _key5 < _len5; _key5++) {
        args[_key5 - 1] = arguments[_key5];
      }

      return args[0] !== null ? _this.dispatch(createSubscription(endpoint, {
        args: args
      })) : Promise.resolve();
    };

    this.unsubscribe = function (endpoint) {
      for (var _len6 = arguments.length, args = new Array(_len6 > 1 ? _len6 - 1 : 0), _key6 = 1; _key6 < _len6; _key6++) {
        args[_key6 - 1] = arguments[_key6];
      }

      return args[0] !== null ? _this.dispatch(createUnsubscription(endpoint, {
        args: args
      })) : Promise.resolve();
    };

    this.snapshot = (state, fetchedAt) => {
      return new Snapshot(this, state, fetchedAt);
    };

    this.getError = function (endpoint) {
      for (var _len7 = arguments.length, rest = new Array(_len7 > 1 ? _len7 - 1 : 0), _key7 = 1; _key7 < _len7; _key7++) {
        rest[_key7 - 1] = arguments[_key7];
      }

      if (rest[0] === null) return;
      const state = rest[rest.length - 1];
      const args = rest.slice(0, rest.length - 1);
      const key = endpoint.key(...args);
      const meta = selectMeta(state, key);
      const results = state.results[key];
      if (results !== undefined && (meta == null ? void 0 : meta.errorPolicy) === 'soft') return;
      return meta == null ? void 0 : meta.error;
    };

    this.getResponse = function (endpoint) {
      for (var _len8 = arguments.length, rest = new Array(_len8 > 1 ? _len8 - 1 : 0), _key8 = 1; _key8 < _len8; _key8++) {
        rest[_key8 - 1] = arguments[_key8];
      }

      const state = rest[rest.length - 1];
      const args = rest.slice(0, rest.length - 1);
      const isActive = args.length !== 1 || args[0] !== null;
      const key = isActive ? endpoint.key(...args) : '';
      const cacheResults = isActive && state.results[key];
      const schema = endpoint.schema;
      const meta = selectMeta(state, key);
      let expiresAt = meta == null ? void 0 : meta.expiresAt;

      const results = _this.getResults(endpoint.schema, cacheResults, args, state.indexes);

      if (!endpoint.schema || !schemaHasEntity(endpoint.schema)) {
        return {
          data: results,
          expiryStatus: meta != null && meta.invalidated ? ExpiryStatus.Invalid : cacheResults && !endpoint.invalidIfStale ? ExpiryStatus.Valid : ExpiryStatus.InvalidIfStale,
          expiresAt: expiresAt || 0
        };
      } // Warn users with bad configurations

      /* istanbul ignore next */


      if (process.env.NODE_ENV !== 'production' && schema && isEntity(schema)) {
        if (Array.isArray(results)) {
          throw new Error(`fetch key ${key} has list results when single result is expected`);
        }

        if (typeof results === 'object') {
          throw new Error(`fetch key ${key} has object results when entity's primary key (string) result is expected`);
        }
      }

      if (isActive && !_this.globalCache.results[key]) _this.globalCache.results[key] = new WeakListMap(); // second argument is false if any entities are missing
      // eslint-disable-next-line prefer-const

      const [data, found, suspend, resolvedEntities] = denormalize(results, schema, state.entities, _this.globalCache.entities, isActive ? _this.globalCache.results[key] : undefined); // fallback to entity expiry time

      if (!expiresAt) {
        // expiresAt existance is equivalent to cacheResults
        if (found) {
          // oldest entity dictates age
          expiresAt = Infinity; // using Object.keys ensures we don't hit `toString` type members

          Object.entries(resolvedEntities).forEach(_ref => {
            let [key, entities] = _ref;
            return Object.keys(entities).forEach(pk => {
              var _state$entityMeta$key, _state$entityMeta$key2, _state$entityMeta$key3;

              expiresAt = Math.min(expiresAt, (_state$entityMeta$key = (_state$entityMeta$key2 = state.entityMeta[key]) == null ? void 0 : (_state$entityMeta$key3 = _state$entityMeta$key2[pk]) == null ? void 0 : _state$entityMeta$key3.expiresAt) != null ? _state$entityMeta$key : Infinity);
            });
          });
        } else {
          expiresAt = 0;
        }
      } // https://resthooks.io/docs/getting-started/expiry-policy#expiry-status
      // we don't track the difference between stale or fresh because that is tied to triggering
      // conditions


      const expiryStatus = meta != null && meta.invalidated || suspend && !(meta != null && meta.error) ? ExpiryStatus.Invalid : suspend || endpoint.invalidIfStale || !cacheResults && !found ? ExpiryStatus.InvalidIfStale : ExpiryStatus.Valid;
      return {
        data,
        expiryStatus,
        expiresAt
      };
    };

    this.getResults = (schema, cacheResults, args, indexes) => {
      if (cacheResults || schema === undefined) return cacheResults;
      return inferResults(schema, args, indexes);
    };

    this.dispatch = dispatch;
    this.globalCache = globalCache;
  }
  /*************** Action Dispatchers ***************/

  /**
   * Fetches the endpoint with given args, updating the Rest Hooks cache with the response or error upon completion.
   * @see https://resthooks.io/docs/api/Controller#fetch
   */


}
/** Determine whether the schema has any entities.
 *
 * Without entities, denormalization is not needed, and results should not be inferred.
 */

function schemaHasEntity(schema) {
  if (isEntity(schema)) return true;
  if (Array.isArray(schema)) return schema.length !== 0 && schemaHasEntity(schema[0]);

  if (schema && (typeof schema === 'object' || typeof schema === 'function')) {
    const nestedSchema = 'schema' in schema ? schema.schema : schema;

    if (typeof nestedSchema === 'function') {
      return schemaHasEntity(nestedSchema);
    }

    return Object.values(nestedSchema).some(x => schemaHasEntity(x));
  }

  return false;
}

class Snapshot {
  constructor(controller, state) {
    var _this2 = this;

    let fetchedAt = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
    this.state = void 0;
    this.controller = void 0;
    this.fetchedAt = void 0;

    this.getResponse = function (endpoint) {
      for (var _len9 = arguments.length, args = new Array(_len9 > 1 ? _len9 - 1 : 0), _key9 = 1; _key9 < _len9; _key9++) {
        args[_key9 - 1] = arguments[_key9];
      }

      return _this2.controller.getResponse(endpoint, ...args, _this2.state);
    };

    this.getError = function (endpoint) {
      for (var _len10 = arguments.length, args = new Array(_len10 > 1 ? _len10 - 1 : 0), _key10 = 1; _key10 < _len10; _key10++) {
        args[_key10 - 1] = arguments[_key10];
      }

      return _this2.controller.getError(endpoint, ...args, _this2.state);
    };

    this.state = state;
    this.controller = controller;
    this.fetchedAt = fetchedAt;
  }
  /*************** Data Access ***************/


}